import React, { useEffect, useState } from 'react';
import { Pencil, Plus, Star, Trash2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ListaReordenable } from '@/components/ui/ListaReordenable';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { confirmDialog } from '@/lib/confirmDialog';
import { useDestinosPago } from '@/presupuestos/hooks/useDestinosPago';
import type { DatoDestinoPago, DestinoPago, DestinoPagoFormData } from '@/presupuestos/types';

const FORM_VACIO: DestinoPagoFormData = {
  nombre: '',
  titulo: '',
  datos: [{ etiqueta: '', valor: '' }],
  leyenda_monto: 'A depositar',
};

const mensajeDeError = (error: any, porDefecto: string): string =>
  error?.response?.data?.message ?? porDefecto;

interface ModalDestinoProps {
  destino: DestinoPago | null;
  abierto: boolean;
  onClose: () => void;
  onGuardar: (data: DestinoPagoFormData) => Promise<unknown>;
  isGuardando: boolean;
}

const ModalDestino: React.FC<ModalDestinoProps> = ({ destino, abierto, onClose, onGuardar, isGuardando }) => {
  const [form, setForm] = useState<DestinoPagoFormData>(FORM_VACIO);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!abierto) return;
    setForm(
      destino
        ? {
            nombre: destino.nombre,
            titulo: destino.titulo,
            datos: destino.datos.map((dato) => ({ ...dato })),
            leyenda_monto: destino.leyendaMonto ?? '',
          }
        : { ...FORM_VACIO, datos: [{ etiqueta: '', valor: '' }] }
    );
    setError('');
  }, [abierto, destino]);

  const setDato = (indice: number, campo: keyof DatoDestinoPago, valor: string) =>
    setForm((actual) => ({
      ...actual,
      datos: actual.datos.map((dato, i) => (i === indice ? { ...dato, [campo]: valor } : dato)),
    }));

  const quitarDato = async (indice: number) => {
    const dato = form.datos[indice];
    const confirmado = await confirmDialog.open({
      title: `¿Quitar el dato ${indice + 1}?`,
      text:
        dato.etiqueta.trim() || dato.valor.trim()
          ? `${dato.etiqueta.trim() || '(sin etiqueta)'}: ${dato.valor.trim() || '(sin valor)'}`
          : 'El dato está vacío.',
      confirmButtonText: 'Quitar',
    });
    if (!confirmado) return;

    setForm((actual) => ({ ...actual, datos: actual.datos.filter((_, i) => i !== indice) }));
  };

  const guardar = async () => {
    const datos = form.datos.filter((dato) => dato.etiqueta.trim() || dato.valor.trim());

    if (!form.nombre.trim() || !form.titulo.trim()) {
      setError('Completá el nombre y el título.');
      return;
    }
    if (datos.some((dato) => !dato.etiqueta.trim() || !dato.valor.trim())) {
      setError('Cada dato de la cuenta necesita etiqueta y valor.');
      return;
    }

    try {
      await onGuardar({
        nombre: form.nombre.trim(),
        titulo: form.titulo.trim(),
        datos,
        leyenda_monto: form.leyenda_monto?.trim() || null,
      });
      onClose();
    } catch (err) {
      setError(mensajeDeError(err, 'No se pudo guardar el destino.'));
    }
  };

  return (
    <Dialog open={abierto} onOpenChange={(open) => !open && !isGuardando && onClose()}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{destino ? 'Editar destino de pago' : 'Nuevo destino de pago'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="destino-nombre">Nombre</Label>
            <Input
              id="destino-nombre"
              value={form.nombre}
              onChange={(event) => setForm({ ...form, nombre: event.target.value })}
              placeholder="Ej.: Banco Galicia"
              maxLength={100}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="destino-titulo">Título en el PDF</Label>
            <Input
              id="destino-titulo"
              value={form.titulo}
              onChange={(event) => setForm({ ...form, titulo: event.target.value })}
              placeholder="Ej.: Efectivo y/o Banco Galicia"
              maxLength={150}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="destino-leyenda">Leyenda del monto</Label>
            <Input
              id="destino-leyenda"
              value={form.leyenda_monto ?? ''}
              onChange={(event) => setForm({ ...form, leyenda_monto: event.target.value })}
              placeholder="Sin leyenda"
              maxLength={50}
            />
            <p className="text-xs text-muted-foreground">
              Se imprime arriba del monto (ej.: A depositar). Vacía, el PDF muestra solo el monto.
            </p>
          </div>

          <div className="space-y-2">
            <Label>Datos de la cuenta</Label>
            {form.datos.map((dato, indice) => (
              <div key={indice} className="flex gap-2">
                <Input
                  aria-label={`Etiqueta ${indice + 1}`}
                  value={dato.etiqueta}
                  onChange={(event) => setDato(indice, 'etiqueta', event.target.value)}
                  placeholder="Etiqueta (ej.: CBU)"
                  className="w-2/5"
                />
                <Input
                  aria-label={`Valor ${indice + 1}`}
                  value={dato.valor}
                  onChange={(event) => setDato(indice, 'valor', event.target.value)}
                  placeholder="Valor"
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={`Quitar dato ${indice + 1}`}
                  onClick={() => void quitarDato(indice)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setForm((actual) => ({ ...actual, datos: [...actual.datos, { etiqueta: '', valor: '' }] }))}
            >
              <Plus className="mr-1 h-4 w-4" />
              Agregar dato
            </Button>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isGuardando}>
            Cancelar
          </Button>
          <Button onClick={() => void guardar()} disabled={isGuardando}>
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Destinos de pago de los presupuestos: las cuentas a las que se deposita el
 * dinero de cada ítem y que se imprimen en el PDF. Los cambios se guardan al
 * momento, sin la barra de "Guardar cambios" de la configuración.
 */
const DestinosPagoSettings: React.FC = () => {
  const {
    destinos,
    isLoading,
    crearDestino,
    actualizarDestino,
    eliminarDestino,
    marcarDefault,
    reordenar,
    isGuardando,
  } = useDestinosPago();
  const [modal, setModal] = useState<{ abierto: boolean; destino: DestinoPago | null }>({
    abierto: false,
    destino: null,
  });

  const ejecutar = async (accion: () => Promise<unknown>, error: string) => {
    try {
      await accion();
    } catch (err) {
      toast.error(mensajeDeError(err, error));
    }
  };

  const eliminar = async (destino: DestinoPago) => {
    const confirmado = await confirmDialog.open({
      title: `¿Eliminar el destino "${destino.nombre}"?`,
      text: 'Esta acción no se puede deshacer.',
      confirmButtonText: 'Eliminar',
    });
    if (confirmado) await ejecutar(() => eliminarDestino(destino.id), 'No se pudo eliminar el destino.');
  };

  if (isLoading) {
    return <p className="py-6 text-center text-sm text-muted-foreground">Cargando destinos de pago...</p>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setModal({ abierto: true, destino: null })} disabled={isGuardando}>
          <Plus className="mr-2 h-4 w-4" />
          Nuevo destino
        </Button>
      </div>

      {destinos.length === 0 && (
        <p className="py-6 text-center text-sm text-muted-foreground">No hay destinos de pago cargados.</p>
      )}

      <ListaReordenable
        as="ul"
        items={destinos}
        onReordenar={(ids) => void ejecutar(() => reordenar(ids), 'No se pudo reordenar los destinos.')}
        etiquetaItem={(destino) => destino.nombre}
        deshabilitado={isGuardando || destinos.length < 2}
        className="space-y-3"
      >
        {(destino, asa) => (
          <div
            data-testid={`destino-${destino.id}`}
            className={`flex items-start gap-2 rounded-md border p-4 ${destino.activo ? 'bg-card' : 'bg-muted'}`}
          >
            <div className="-ml-2">{asa}</div>
            <div className="flex flex-1 flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{destino.nombre}</span>
                  {destino.esDefault && <Badge>Por defecto</Badge>}
                  {!destino.activo && <Badge variant="secondary">Inactivo</Badge>}
                  {destino.enUso && <Badge variant="outline">En uso</Badge>}
                </div>
                <p className="text-sm text-muted-foreground">{destino.titulo}</p>
                <p className="text-xs text-muted-foreground">
                  {destino.leyendaMonto ? `Leyenda del monto: ${destino.leyendaMonto}` : 'Sin leyenda del monto'}
                </p>
                <dl className="grid grid-cols-[auto_1fr] gap-x-3 text-sm">
                  {destino.datos.map((dato, i) => (
                    <React.Fragment key={i}>
                      <dt className="text-muted-foreground">{dato.etiqueta}:</dt>
                      <dd className="break-all">{dato.valor}</dd>
                    </React.Fragment>
                  ))}
                </dl>
              </div>

              <div className="flex flex-wrap items-center gap-1">
                {!destino.esDefault && (
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Usar ${destino.nombre} por defecto`}
                    title={destino.activo ? 'Usar por defecto' : 'Activalo para usarlo por defecto'}
                    disabled={!destino.activo || isGuardando}
                    onClick={() =>
                      void ejecutar(() => marcarDefault(destino.id), 'No se pudo cambiar el destino por defecto.')
                    }
                  >
                    <Star className="h-4 w-4" />
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  disabled={destino.esDefault || isGuardando}
                  title={destino.esDefault ? 'El destino por defecto no se puede desactivar' : undefined}
                  onClick={() =>
                    void ejecutar(
                      () => actualizarDestino({ id: destino.id, data: { activo: !destino.activo } }),
                      'No se pudo actualizar el destino.'
                    )
                  }
                >
                  {destino.activo ? 'Desactivar' : 'Activar'}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Editar ${destino.nombre}`}
                  disabled={isGuardando}
                  onClick={() => setModal({ abierto: true, destino })}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Eliminar ${destino.nombre}`}
                  title={
                    destino.esDefault
                      ? 'El destino por defecto no se puede eliminar'
                      : destino.enUso
                        ? 'Hay ítems que lo usan: solo se puede desactivar'
                        : undefined
                  }
                  disabled={destino.esDefault || destino.enUso || isGuardando}
                  className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                  onClick={() => void eliminar(destino)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </ListaReordenable>

      <ModalDestino
        abierto={modal.abierto}
        destino={modal.destino}
        onClose={() => setModal({ abierto: false, destino: null })}
        isGuardando={isGuardando}
        onGuardar={(data) =>
          modal.destino ? actualizarDestino({ id: modal.destino.id, data }) : crearDestino(data)
        }
      />
    </div>
  );
};

export default DestinosPagoSettings;
